import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  KeyRound, Loader2, ShieldAlert, XCircle, MonitorSmartphone, Laptop, Smartphone,
  RotateCcw, Unlink, X, CheckCircle2,
} from "lucide-react";
import {
  fetchLicenciaActualAdmin, revocarLicenciaAdmin, fetchDispositivosAdmin,
  desvincularDispositivoAdmin, reactivarDispositivoAdmin,
} from "../../lib/licenciasAdmin";

const GOLD = "#B45309";

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

const ESTADO_STYLE = {
  ACTIVA: { l: "ACTIVA", c: "bg-emerald-100 text-emerald-700" },
  VENCIDA: { l: "VENCIDA", c: "bg-amber-100 text-amber-700" },
  REVOCADA: { l: "REVOCADA", c: "bg-rose-100 text-rose-700" },
  SIN_LICENCIA: { l: "SIN LICENCIA", c: "bg-slate-100 text-slate-500" },
};

function EstadoLicChip({ estado }) {
  const s = ESTADO_STYLE[estado] || ESTADO_STYLE.SIN_LICENCIA;
  return <span data-testid="lic-actual-estado" className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${s.c}`}>{s.l}</span>;
}

// Modal de confirmación de revocación
function RevocarModal({ nombre, onConfirm, onCancel, busy }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="lic-revocar-modal">
      <div className="bg-white rounded-2xl shadow-lifted max-w-md w-full p-6">
        <div className="flex items-center gap-2 text-rose-600"><ShieldAlert size={20} /><h3 className="font-bold text-slate-900">Revocar licencia</h3></div>
        <p className="text-sm text-slate-600 mt-3">
          Esta acción retirará el acceso licenciado al programa. Los proyectos y datos del usuario <b>NO</b> serán eliminados.
        </p>
        <div className="mt-5 flex gap-2">
          <button onClick={onCancel} disabled={busy} data-testid="lic-revocar-cancel" className="flex-1 py-2.5 rounded-xl font-bold text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">Cancelar</button>
          <button onClick={onConfirm} disabled={busy} data-testid="lic-revocar-confirm" className="flex-1 py-2.5 rounded-xl font-bold text-sm text-white bg-rose-600 hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />} Revocar acceso
          </button>
        </div>
      </div>
    </div>
  );
}

function DispositivoRow({ d, onDesvincular, onReactivar, busyId }) {
  const isWin = (d.platform || "").toLowerCase() === "windows";
  const Icon = isWin ? Laptop : Smartphone;
  const activo = d.active;
  const busy = busyId === d.id;
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3" data-testid={`lic-device-${d.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${activo ? "bg-brand-blue/10 text-brand-blue" : "bg-slate-100 text-slate-400"}`}><Icon size={18} /></span>
        <div className="min-w-0">
          <p className="font-bold text-slate-800 text-sm truncate">{d.device_name || "Dispositivo"} <span className="text-[10px] font-bold uppercase text-slate-400">· {isWin ? "Windows" : "Android"}</span></p>
          <p className="text-[11px] text-slate-500 truncate">ID {d.device_id_masked || "—"} · alta {fmt(d.first_seen_at)} · último uso {fmt(d.last_seen_at)}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${activo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`lic-device-estado-${d.id}`}>{activo ? "ACTIVO" : "DESVINCULADO"}</span>
        {activo ? (
          <button onClick={() => onDesvincular(d)} disabled={busy} data-testid={`lic-device-desvincular-${d.id}`} className="text-xs font-bold text-rose-600 border border-rose-200 rounded-lg px-2.5 py-1.5 hover:bg-rose-50 inline-flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Unlink size={12} />} Desvincular
          </button>
        ) : (
          <button onClick={() => onReactivar(d)} disabled={busy} data-testid={`lic-device-reactivar-${d.id}`} className="text-xs font-bold text-brand-blue border border-brand-blue/30 rounded-lg px-2.5 py-1.5 hover:bg-brand-blue/5 inline-flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Reactivar
          </button>
        )}
      </div>
    </div>
  );
}

// Modal de gestión de dispositivos
function DispositivosModal({ userId, slug, nombre, maxDevices, onClose, onChanged }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try { setDevices(await fetchDispositivosAdmin(userId, slug) || []); }
    catch (_) { toast.error("No se pudieron cargar los dispositivos."); }
    finally { setLoading(false); }
  }, [userId, slug]);

  useEffect(() => { cargar(); }, [cargar]);

  const activos = devices.filter((d) => d.active).length;

  async function desvincular(d) {
    setBusyId(d.id);
    try { await desvincularDispositivoAdmin(d.id); toast.success("Dispositivo desvinculado. Cupo liberado."); await cargar(); onChanged && onChanged(); }
    catch (_) { toast.error("No se pudo desvincular."); }
    finally { setBusyId(null); }
  }

  async function reactivar(d) {
    setBusyId(d.id);
    try { await reactivarDispositivoAdmin(d.id); toast.success("Dispositivo reactivado."); await cargar(); onChanged && onChanged(); }
    catch (e) {
      if (e?.response?.status === 409) toast.error("Límite de dispositivos alcanzado.");
      else toast.error("No se pudo reactivar.");
    }
    finally { setBusyId(null); }
  }

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4" data-testid="lic-dispositivos-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lifted max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div className="flex items-center gap-2"><MonitorSmartphone size={18} className="text-brand-blue" /><h3 className="font-bold text-slate-900">Dispositivos · {nombre}</h3></div>
          <button onClick={onClose} data-testid="lic-dispositivos-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={18} /></button>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">Dispositivos activos (compartidos Windows + Android)</p>
            <span className="text-sm font-extrabold text-slate-900" data-testid="lic-dispositivos-contador">{activos} / {maxDevices}</span>
          </div>
          {loading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
          ) : devices.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center bg-slate-50 rounded-xl border border-slate-100" data-testid="lic-dispositivos-vacio">Este usuario no tiene dispositivos registrados para este programa.</p>
          ) : (
            <div className="space-y-2">
              {devices.map((d) => (
                <DispositivoRow key={d.id} d={d} busyId={busyId} onDesvincular={desvincular} onReactivar={reactivar} />
              ))}
            </div>
          )}
          <p className="mt-4 text-[11px] text-slate-400">"Reactivar" solo vuelve a habilitar un dispositivo ya conocido (mismo usuario y programa) si hay cupo. Los dispositivos nuevos se vinculan automáticamente al iniciar sesión desde la app.</p>
        </div>
      </div>
    </div>
  );
}

// Bloque reutilizable "LICENCIA ACTUAL" (inline en Licencias de Programa; y dentro del modal del Historial)
export function LicenciaActual({ userId, slug, nombre, onChanged }) {
  const [lic, setLic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRevocar, setShowRevocar] = useState(false);
  const [showDispositivos, setShowDispositivos] = useState(false);
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async () => {
    if (!userId || !slug) return;
    setLoading(true);
    try { setLic(await fetchLicenciaActualAdmin(userId, slug)); }
    catch (_) { setLic(null); }
    finally { setLoading(false); }
  }, [userId, slug]);

  useEffect(() => { cargar(); }, [cargar]);

  async function confirmarRevocar() {
    setBusy(true);
    try {
      await revocarLicenciaAdmin(userId, slug);
      toast.success("Licencia revocada. El acceso licenciado fue retirado.");
      setShowRevocar(false);
      await cargar();
      onChanged && onChanged();
    } catch (_) { toast.error("No se pudo revocar."); }
    finally { setBusy(false); }
  }

  if (!userId || !slug) return null;

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/60 p-5" data-testid="lic-actual-bloque">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-extrabold text-slate-900 inline-flex items-center gap-2 text-sm uppercase tracking-wide"><KeyRound size={16} style={{ color: GOLD }} /> Licencia actual</h3>
        {loading ? <Loader2 size={16} className="animate-spin text-slate-400" /> : lic && <EstadoLicChip estado={lic.estado} />}
      </div>

      {loading ? (
        <div className="py-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : !lic || lic.estado === "SIN_LICENCIA" ? (
        <p className="text-sm text-slate-500 py-2" data-testid="lic-actual-sin-licencia">Este usuario no tiene licencia vigente para este programa.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div><p className="text-[10px] font-bold uppercase text-slate-400">Programa</p><p className="font-bold text-slate-900 truncate">{lic.programa_nombre}</p></div>
            <div><p className="text-[10px] font-bold uppercase text-slate-400">Activación</p><p className="font-semibold text-slate-700">{fmt(lic.fecha_activacion)}</p></div>
            <div><p className="text-[10px] font-bold uppercase text-slate-400">Vencimiento</p><p className="font-semibold text-slate-700">{fmt(lic.fecha_vencimiento)}</p></div>
            <div><p className="text-[10px] font-bold uppercase text-slate-400">Origen</p><p className="font-semibold text-slate-700">{lic.origen || "—"}</p></div>
            <div><p className="text-[10px] font-bold uppercase text-slate-400">Máx. dispositivos</p><p className="font-semibold text-slate-700">{lic.max_devices}</p></div>
            <div><p className="text-[10px] font-bold uppercase text-slate-400">Dispositivos activos</p><p className="font-bold text-slate-900" data-testid="lic-actual-devices">{lic.active_devices} / {lic.max_devices}</p></div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => setShowDispositivos(true)} data-testid="lic-actual-gestionar-dispositivos" className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-blue border border-brand-blue/30 rounded-xl px-3.5 py-2 hover:bg-brand-blue/5">
              <MonitorSmartphone size={15} /> Gestionar dispositivos
            </button>
            {lic.estado !== "REVOCADA" && (
              <button onClick={() => setShowRevocar(true)} data-testid="lic-actual-revocar" className="inline-flex items-center gap-1.5 text-sm font-bold text-rose-600 border border-rose-200 rounded-xl px-3.5 py-2 hover:bg-rose-50">
                <XCircle size={15} /> Revocar licencia
              </button>
            )}
            {lic.estado === "REVOCADA" && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-500"><CheckCircle2 size={15} /> Acceso revocado</span>
            )}
          </div>
        </>
      )}

      {showRevocar && <RevocarModal nombre={nombre} busy={busy} onConfirm={confirmarRevocar} onCancel={() => setShowRevocar(false)} />}
      {showDispositivos && lic && (
        <DispositivosModal userId={userId} slug={slug} nombre={nombre || lic.programa_nombre} maxDevices={lic.max_devices}
          onClose={() => setShowDispositivos(false)} onChanged={cargar} />
      )}
    </div>
  );
}

// Wrapper modal para el Historial (ubicación B): abre el MISMO gestor de licencia + dispositivos.
export function GestorLicenciaModal({ open, userId, slug, nombre, onClose, onChanged }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="lic-gestor-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lifted max-w-xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div className="flex items-center gap-2"><KeyRound size={18} style={{ color: GOLD }} /><h3 className="font-bold text-slate-900">Gestionar licencia · {nombre}</h3></div>
          <button onClick={onClose} data-testid="lic-gestor-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={18} /></button>
        </div>
        <div className="px-6 pb-6">
          <LicenciaActual userId={userId} slug={slug} nombre={nombre} onChanged={onChanged} />
        </div>
      </div>
    </div>
  );
}
